/* assets/hm-store-locator.js
 * Store locator controller: Google Maps + Places search, geolocation,
 * department filter sheet, distance sort, and card <-> pin <-> store-information
 * sheet sync. All store data is rendered server-side; this module enhances it.
 */

const ROOT = document.querySelector('[data-store-locator]');

/* Above this many markers the map switches to clustering (loaded on demand).
 * One paginated page holds up to 250 stores; for the H&M TH/ID datasets (well
 * under 250, a single page) the stitch + clustering paths below never execute. */
const CLUSTER_THRESHOLD = 100;
const CLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';

function readJSON(node, fallback) {
  if (!node) return fallback;
  try {
    return JSON.parse(node.textContent) || fallback;
  } catch (error) {
    console.error('[store-locator] invalid JSON', error);
    return fallback;
  }
}

function haversineKm(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

function directionsUrl(store) {
  return `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
}

class StoreLocator {
  constructor(root) {
    this.root = root;
    this.stores = readJSON(root.querySelector('[data-store-locator-data]'), []);
    this.departmentLabels = readJSON(root.querySelector('[data-department-labels]'), {});

    this.mapEl = root.querySelector('[data-map]');
    this.mapHome = this.mapEl ? this.mapEl.parentElement : null; /* map-frame; the map returns here after the sheet Map tab closes */
    this.selectedCardHolder = root.querySelector('[data-map-selected]');
    this.listEl = root.querySelector('[data-store-list]');
    this.countEl = root.querySelector('[data-store-count]');
    this.searchEl = root.querySelector('[data-store-search]');
    this.resultsEl = root.querySelector('[data-search-results]');
    this.recentKey = root.dataset.recentKey || '';
    this.activeIndex = -1;
    this.layoutEl = root.querySelector('[data-store-view]');
    this.sheetEl = root.querySelector('[data-store-detail-sheet]');
    this.sheetMapPanel = this.sheetEl ? this.sheetEl.querySelector('[data-sheet-map]') : null;
    this.blanketEl = root.querySelector('[data-sheet-blanket]');
    this.filterSheetEl = root.querySelector('[data-filter-sheet]');
    this.filterBlanketEl = root.querySelector('[data-filter-blanket]');
    this.filterTriggerEl = root.querySelector('[data-open-filter]');
    this.filterCountEl = root.querySelector('[data-filter-trigger-count]');

    this.apiKey = this.mapEl ? this.mapEl.dataset.apiKey : '';
    this.markerIconUrl = this.mapEl ? this.mapEl.dataset.markerIconUrl || '' : '';
    this.markerIconWidth = this.parsePositiveNumber(this.mapEl ? this.mapEl.dataset.markerIconWidth : '');
    this.markerIconHeight = this.parsePositiveNumber(this.mapEl ? this.mapEl.dataset.markerIconHeight : '');
    this.locale = this.mapEl ? this.mapEl.dataset.locale || '' : '';
    this.activeDepartments = [];
    /* null = no filter applied yet (initial = all checked, Figma default).
     * After Apply it holds the exact checked handles (even an empty array, so a
     * "clear all" choice is remembered when the sheet is reopened). */
    this.selected = null;
    this.selectedCount = this.root.querySelectorAll('[data-filter-option]').length;
    this.center = null;
    this.map = null;
    this.markers = new Map();
    this.clusterer = null;
    this.lastFocus = null;
    this.currentStoreId = null;
    this.viewBeforeSheet = 'list';

    this.bindEvents();
    this.updateFilterTrigger();
    this.applyFilter();
    this.stitchRemainingPages();
    this.initSearch(); /* recent + store-match work without Maps; place predictions enabled later */

    if (this.apiKey) {
      this.loadGoogleMaps().then(() => this.initMap()).catch((error) => {
        console.error('[store-locator] Google Maps failed to load', error);
      });
    }
  }

  parsePositiveNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  getMarkerIcon() {
    if (!this.markerIconUrl) return null;
    const icon = { url: this.markerIconUrl };
    if (this.markerIconWidth && this.markerIconHeight && window.google && window.google.maps) {
      icon.scaledSize = new window.google.maps.Size(this.markerIconWidth, this.markerIconHeight);
      icon.anchor = new window.google.maps.Point(this.markerIconWidth / 2, this.markerIconHeight);
    }
    return icon;
  }

  bindEvents() {
    if (this.filterTriggerEl) {
      this.filterTriggerEl.addEventListener('click', () => this.openFilter());
    }
    this.root.querySelectorAll('[data-filter-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeFilter());
    });
    if (this.filterBlanketEl) this.filterBlanketEl.addEventListener('click', () => this.closeFilter());

    const allBox = this.root.querySelector('[data-filter-all]');
    if (allBox) {
      allBox.addEventListener('change', () => {
        allBox.indeterminate = false;
        this.setOptionBoxes(allBox.checked);
      });
    }
    this.root.querySelectorAll('[data-filter-option]').forEach((box) => {
      box.addEventListener('change', () => this.syncParent());
    });
    const clearBtn = this.root.querySelector('[data-filter-clear]');
    if (clearBtn) clearBtn.addEventListener('click', () => this.clearFilter());
    const applyBtn = this.root.querySelector('[data-filter-apply]');
    if (applyBtn) applyBtn.addEventListener('click', () => this.applyFilterSelection());

    this.root.querySelectorAll('[data-view-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => this.setView(btn.dataset.viewToggle));
    });

    this.root.querySelectorAll('[data-sheet-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this.setSheetTab(tab.dataset.sheetTab));
    });

    /* Single-open accordion (list view): opening one store card closes the rest.
     * 'toggle' doesn't bubble, so listen in the capture phase to also cover cards
     * appended later by stitchRemainingPages(). */
    this.root.addEventListener(
      'toggle',
      (event) => {
        const opened = event.target;
        if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
        if (!opened.classList.contains('hm-store-card')) return;
        this.root.querySelectorAll('details.hm-store-card[open]').forEach((card) => {
          if (card !== opened) card.open = false;
        });
      },
      true
    );

    const nearMe = this.root.querySelector('[data-near-me]');
    if (nearMe) nearMe.addEventListener('click', () => this.locateMe());

    /* Delegated so cards appended by stitchRemainingPages() are covered too. */
    this.root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-open-sheet]');
      if (trigger && this.root.contains(trigger)) this.openSheet(trigger.dataset.openSheet);
    });

    if (this.sheetEl) {
      const closeBtn = this.sheetEl.querySelector('[data-sheet-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closeSheet());
    }
    if (this.blanketEl) this.blanketEl.addEventListener('click', () => this.closeSheet());

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.closeSheet();
      this.closeFilter();
      this.closeResults();
    });
  }

  /* ---- Department filter sheet ---- */

  openFilter() {
    if (!this.filterSheetEl) return;
    this.syncFilterBoxes();
    this.lastFocus = document.activeElement;
    this.filterSheetEl.hidden = false;
    this.filterSheetEl.setAttribute('aria-hidden', 'false');
    if (this.filterBlanketEl) this.filterBlanketEl.hidden = false;
    const first = this.filterSheetEl.querySelector('[data-filter-close]');
    if (first) first.focus();
  }

  closeFilter() {
    if (!this.filterSheetEl || this.filterSheetEl.hidden) return;
    this.filterSheetEl.hidden = true;
    this.filterSheetEl.setAttribute('aria-hidden', 'true');
    if (this.filterBlanketEl) this.filterBlanketEl.hidden = true;
    if (this.filterTriggerEl) this.filterTriggerEl.focus();
  }

  setOptionBoxes(checked) {
    this.root.querySelectorAll('[data-filter-option]').forEach((box) => {
      box.checked = checked;
    });
  }

  syncParent() {
    const options = Array.from(this.root.querySelectorAll('[data-filter-option]'));
    const checked = options.filter((b) => b.checked).length;
    const allBox = this.root.querySelector('[data-filter-all]');
    if (!allBox) return;
    allBox.checked = checked === options.length;
    allBox.indeterminate = checked > 0 && checked < options.length;
  }

  syncFilterBoxes() {
    const options = Array.from(this.root.querySelectorAll('[data-filter-option]'));
    const allBox = this.root.querySelector('[data-filter-all]');
    if (this.selected === null) {
      /* Initial state only: all checked (Figma default). */
      options.forEach((box) => {
        box.checked = true;
      });
      if (allBox) {
        allBox.checked = true;
        allBox.indeterminate = false;
      }
      return;
    }
    /* Restore the exact applied selection — so "clear all" stays cleared on reopen. */
    const selected = new Set(this.selected);
    options.forEach((box) => {
      box.checked = selected.has(box.value);
    });
    this.syncParent();
  }

  clearFilter() {
    this.setOptionBoxes(false);
    const allBox = this.root.querySelector('[data-filter-all]');
    if (allBox) {
      allBox.checked = false;
      allBox.indeterminate = false;
    }
  }

  applyFilterSelection() {
    const options = Array.from(this.root.querySelectorAll('[data-filter-option]'));
    const checked = options.filter((box) => box.checked);
    this.selected = checked.map((box) => box.value); /* remembered for reopen (incl. empty) */
    this.selectedCount = checked.length;
    if (checked.length === 0 || checked.length === options.length) {
      /* all or none selected = no department constraint (show every store). */
      this.activeDepartments = [];
    } else {
      this.activeDepartments = checked.map((box) => box.value);
    }
    this.updateFilterTrigger();
    this.applyFilter();
    this.closeFilter();
  }

  /* Badge always shows the number of selected departments — including when all
   * are ticked (= total) or none are (= 0), per the Filter-bar "[ N ]" in Figma. */
  updateFilterTrigger() {
    if (!this.filterCountEl) return;
    this.filterCountEl.hidden = false;
    this.filterCountEl.textContent = `[ ${this.selectedCount} ]`;
  }

  matchesDepartment(store) {
    if (this.activeDepartments.length === 0) return true;
    const list = Array.isArray(store.departments) ? store.departments.map((d) => String(d)) : [];
    return this.activeDepartments.some((dep) => list.includes(dep));
  }

  visibleStores() {
    let list = this.stores.filter((store) => this.matchesDepartment(store));
    if (this.center) {
      list = list
        .map((store) => ({ store, distance: haversineKm(this.center, store) }))
        .sort((a, b) => a.distance - b.distance)
        .map((entry) => entry.store);
    }
    return list;
  }

  applyFilter() {
    const visible = this.visibleStores();
    const visibleIds = new Set(visible.map((store) => store.id));

    const cards = Array.from(this.root.querySelectorAll('[data-store-card]'));
    cards.forEach((card) => {
      card.hidden = !visibleIds.has(card.dataset.storeId);
    });
    visible.forEach((store) => {
      const card = cards.find((item) => item.dataset.storeId === store.id);
      if (card && this.listEl) this.listEl.appendChild(card);
    });

    if (this.clusterer) {
      const visibleMarkers = [];
      this.markers.forEach((marker, id) => {
        if (visibleIds.has(id)) {
          visibleMarkers.push(marker);
        } else {
          marker.setMap(null);
        }
      });
      this.clusterer.clearMarkers();
      this.clusterer.addMarkers(visibleMarkers);
    } else {
      this.markers.forEach((marker, id) => {
        marker.setMap(visibleIds.has(id) ? this.map : null);
      });
    }

    this.updateCount(visible.length);
  }

  departmentLabel(key) {
    return this.departmentLabels[String(key)] || key;
  }

  updateCount(count) {
    if (!this.countEl) return;
    const template = this.countEl.dataset.countTemplate;
    if (template) this.countEl.textContent = template.replace('%count%', String(count));
  }

  /* ---- View toggle (mobile) ---- */

  isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  currentView() {
    return this.layoutEl ? this.layoutEl.getAttribute('data-store-view') : 'list';
  }

  setView(view) {
    if (this.layoutEl) this.layoutEl.setAttribute('data-store-view', view);
    this.root.querySelectorAll('[data-view-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.viewToggle === view));
    });
    if (view === 'map') {
      if (this.map && window.google) {
        window.google.maps.event.trigger(this.map, 'resize');
        this.fitBounds();
      }
      /* Mobile: keep the currently-open store's card visible below the map (Figma --13). */
      if (this.isMobile()) {
        const open = this.root.querySelector('details.hm-store-card[open]');
        const wrapper = open ? open.closest('[data-store-card]') : null;
        if (wrapper) this.showSelectedCardBelowMap(wrapper.dataset.storeId);
      }
    } else if (view === 'list') {
      this.restoreSelectedCard();
    }
  }

  /* Move the selected store's (open) card into the holder below the map. */
  showSelectedCardBelowMap(storeId) {
    if (!this.selectedCardHolder) return;
    const card = this.root.querySelector(`[data-store-card][data-store-id="${storeId}"]`);
    if (!card) return;
    this.selectedCardHolder.appendChild(card);
    const details = card.querySelector('details.hm-store-card');
    if (details) details.open = true;
  }

  /* Return any card parked below the map back into the list in canonical order. */
  restoreSelectedCard() {
    if (!this.selectedCardHolder || !this.selectedCardHolder.children.length) return;
    Array.from(this.selectedCardHolder.children).forEach((card) => {
      if (this.listEl) this.listEl.appendChild(card);
    });
    this.applyFilter();
  }

  setSheetTab(tab) {
    if (!this.sheetEl) return;
    this.sheetEl.querySelectorAll('[data-sheet-tab]').forEach((btn) => {
      btn.setAttribute('aria-selected', String(btn.dataset.sheetTab === tab));
    });
    this.sheetEl.querySelectorAll('[data-sheet-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.sheetPanel !== tab;
    });
    if (tab === 'map') {
      this.mountMapInSheet();
      if (this.currentStoreId) this.highlightMarker(this.currentStoreId);
    } else {
      this.unmountMapFromSheet();
    }
  }

  /* Relocate the single page map into the sheet's Map panel (mobile only — on
   * desktop the map stays in the always-visible left column). */
  mountMapInSheet() {
    if (!this.isMobile() || !this.sheetMapPanel || !this.mapEl) return;
    this.sheetMapPanel.appendChild(this.mapEl);
    if (this.map && window.google) {
      window.google.maps.event.trigger(this.map, 'resize');
      const store = this.stores.find((item) => item.id === this.currentStoreId);
      if (store) this.map.setCenter({ lat: store.lat, lng: store.lng });
    }
  }

  /* Return the map element to its home (map column) after the Map tab closes. */
  unmountMapFromSheet() {
    if (!this.mapEl || !this.mapHome) return;
    if (this.mapEl.parentElement === this.mapHome) return;
    this.mapHome.insertBefore(this.mapEl, this.mapHome.firstChild);
    if (this.map && window.google) window.google.maps.event.trigger(this.map, 'resize');
  }

  /* ---- Geolocation ---- */

  locateMe() {
    if (!navigator.geolocation) {
      this.announce(this.root.dataset.geoError);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.center = { lat: position.coords.latitude, lng: position.coords.longitude };
        this.applyFilter();
        this.recenter();
      },
      () => this.announce(this.root.dataset.geoError)
    );
  }

  announce(message) {
    if (message) window.alert(message);
  }

  /* ---- Google Maps ---- */

  loadGoogleMaps() {
    if (window.google && window.google.maps) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const params = new URLSearchParams({ key: this.apiKey, libraries: 'places' });
      if (this.locale) params.set('language', this.locale);
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  initMap() {
    const fallback = this.stores[0] || { lat: 13.7563, lng: 100.5018 };
    this.map = new window.google.maps.Map(this.mapEl, {
      center: { lat: fallback.lat, lng: fallback.lng },
      zoom: 11,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    this.syncMarkers();
    this.enablePlaces();
    this.applyFilter();
    this.fitBounds();
  }

  /* Create a Google Maps marker for every store not already on the map. Called
   * from initMap and again after stitchRemainingPages() adds more stores. */
  syncMarkers() {
    if (!this.map || !window.google) return;
    this.stores.forEach((store) => {
      if (this.markers.has(store.id)) return;
      if (typeof store.lat !== 'number' || typeof store.lng !== 'number') return;
      const markerOptions = {
        position: { lat: store.lat, lng: store.lng },
        title: store.name,
      };
      const icon = this.getMarkerIcon();
      if (icon) markerOptions.icon = icon;
      const marker = new window.google.maps.Marker(markerOptions);
      marker.addListener('click', () => this.handlePinClick(store.id));
      this.markers.set(store.id, marker);
    });
    this.maybeCluster();
  }

  /* Beyond CLUSTER_THRESHOLD markers, load the Google Maps marker clusterer
   * (on demand) and let it manage marker placement. Dormant for small datasets. */
  maybeCluster() {
    if (this.clusterer || !this.map) return;
    if (this.markers.size <= CLUSTER_THRESHOLD) return;
    this.loadClusterer()
      .then(() => {
        if (!window.markerClusterer) return;
        this.markers.forEach((marker) => marker.setMap(null));
        this.clusterer = new window.markerClusterer.MarkerClusterer({ map: this.map, markers: [] });
        this.applyFilter();
      })
      .catch((error) => console.error('[store-locator] clusterer failed to load', error));
  }

  loadClusterer() {
    if (window.markerClusterer) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = CLUSTERER_SRC;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /* When the dataset spans more than one paginated page (>250 stores), eager-load
   * the remaining pages via the Section Rendering API and stitch their cards +
   * JSON islands into the single scrollable list. No-op for a single page. */
  stitchRemainingPages() {
    const total = parseInt((this.listEl && this.listEl.dataset.totalPages) || '1', 10);
    const sectionId = this.listEl && this.listEl.dataset.sectionId;
    if (!this.listEl || !sectionId || !(total > 1)) return;

    const path = window.location.pathname;
    const seen = new Set(this.stores.map((store) => store.id));

    const loadPage = async (page) => {
      try {
        const res = await fetch(`${path}?section_id=${encodeURIComponent(sectionId)}&page=${page}`);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        doc.querySelectorAll('[data-store-card]').forEach((card) => {
          if (!seen.has(card.dataset.storeId)) this.listEl.appendChild(card);
        });
        readJSON(doc.querySelector('[data-store-locator-data]'), []).forEach((store) => {
          if (!seen.has(store.id)) {
            this.stores.push(store);
            seen.add(store.id);
          }
        });
      } catch (error) {
        console.error('[store-locator] stitch page failed', page, error);
      }
    };

    const tasks = [];
    for (let page = 2; page <= total; page += 1) tasks.push(loadPage(page));
    Promise.all(tasks).then(() => {
      if (this.map) this.syncMarkers();
      this.applyFilter();
    });
  }

  /* Custom Recent + Suggestion dropdown (Figma Search-result). Replaces the native
   * google.maps.places.Autocomplete: empty input shows recent searches (localStorage);
   * typing shows place predictions (pin) + matching H&M stores (logo). */
  initSearch() {
    if (!this.searchEl || !this.resultsEl) return;
    this.searchIconPlace = this.root.querySelector('[data-search-icon-place]');
    this.searchIconStore = this.root.querySelector('[data-search-icon-store]');

    let debounce;
    this.searchEl.addEventListener('focus', () => {
      if (this.searchEl.value.trim() === '') this.renderRecent();
    });
    this.searchEl.addEventListener('input', () => {
      const query = this.searchEl.value.trim();
      clearTimeout(debounce);
      if (query === '') {
        this.renderRecent();
        return;
      }
      debounce = setTimeout(() => this.renderSuggestions(query), 250);
    });
    this.searchEl.addEventListener('keydown', (event) => this.onSearchKeydown(event));
    /* Outside-click closes the dropdown (the search wrapper is the relative parent). */
    document.addEventListener('click', (event) => {
      const wrap = this.searchEl.closest('.hm-store-locator__search');
      if (wrap && !wrap.contains(event.target)) this.closeResults();
    });
  }

  /* Enable Google Places location predictions once Maps is loaded (needs an API key).
   * Without it, suggestions fall back to H&M store matches + recent (no place rows). */
  enablePlaces() {
    if (!window.google || !window.google.maps.places || !this.map) return;
    this.acService = new window.google.maps.places.AutocompleteService();
    this.placesService = new window.google.maps.places.PlacesService(this.map);
  }

  readRecent() {
    if (!this.recentKey) return [];
    try {
      return JSON.parse(window.localStorage.getItem(this.recentKey) || '[]') || [];
    } catch (error) {
      return [];
    }
  }

  pushRecent(entry) {
    if (!this.recentKey) return;
    const item = { type: entry.type, label: entry.label, placeId: entry.placeId, storeId: entry.storeId };
    let list = this.readRecent().filter((existing) => existing.label !== item.label);
    list.unshift(item);
    list = list.slice(0, 5); /* keep the 5 most recent */
    try {
      window.localStorage.setItem(this.recentKey, JSON.stringify(list));
    } catch (error) {
      /* storage full / disabled — ignore */
    }
  }

  renderRecent() {
    const items = this.readRecent();
    if (!items.length) {
      this.closeResults();
      return;
    }
    const frag = document.createDocumentFragment();
    const heading = document.createElement('p');
    heading.className = 'hm-store-locator__search-heading';
    heading.textContent = this.root.dataset.recentHeading || '';
    frag.appendChild(heading);
    /* Recent rows are text-only (Figma history variant). */
    items.forEach((entry, index) => frag.appendChild(this.buildSearchRow(entry, index, false)));
    this.fillResults(frag);
  }

  renderSuggestions(query) {
    const needle = query.toLowerCase();
    const stores = this.stores
      .filter((store) => {
        const hay = `${store.name || ''} ${store.address1 || ''} ${store.address2 || ''} ${store.district || ''} ${store.city || ''} ${store.postal || ''}`.toLowerCase();
        return hay.includes(needle);
      })
      .slice(0, 5)
      .map((store) => ({ type: 'store', label: store.name, storeId: store.id }));

    /* Figma order: place predictions first, store matches second. */
    const render = (places) => {
      const rows = places.concat(stores);
      if (!rows.length) {
        this.renderEmpty();
        return;
      }
      const frag = document.createDocumentFragment();
      rows.forEach((entry, index) => frag.appendChild(this.buildSearchRow(entry, index, true)));
      this.fillResults(frag);
    };

    /* No API key / Maps not loaded → store matches only (place predictions need Places). */
    if (!this.acService) {
      render([]);
      return;
    }
    this.acService.getPlacePredictions({ input: query }, (predictions, status) => {
      let places = [];
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        places = predictions
          .slice(0, 5)
          .map((prediction) => ({ type: 'place', label: prediction.description, placeId: prediction.place_id }));
      }
      render(places);
    });
  }

  renderEmpty() {
    const frag = document.createDocumentFragment();
    const message = document.createElement('p');
    message.className = 'hm-store-locator__search-heading';
    message.textContent = this.root.dataset.noResults || '';
    frag.appendChild(message);
    this.fillResults(frag);
  }

  buildSearchRow(entry, index, withIcon) {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'hm-store-locator__search-row';
    row.setAttribute('role', 'option');
    row.setAttribute('aria-selected', 'false');
    row.dataset.index = String(index);
    if (withIcon) {
      const template = entry.type === 'store' ? this.searchIconStore : this.searchIconPlace;
      if (template && template.content) row.appendChild(template.content.cloneNode(true));
    }
    const label = document.createElement('span');
    label.textContent = entry.label;
    row.appendChild(label);
    row.addEventListener('click', () => this.selectSearchEntry(entry));
    return row;
  }

  fillResults(frag) {
    this.resultsEl.innerHTML = '';
    this.resultsEl.appendChild(frag);
    this.activeIndex = -1;
    this.openResults();
  }

  openResults() {
    this.resultsEl.hidden = false;
    this.searchEl.setAttribute('aria-expanded', 'true');
  }

  closeResults() {
    if (!this.resultsEl || this.resultsEl.hidden) return;
    this.resultsEl.hidden = true;
    this.searchEl.setAttribute('aria-expanded', 'false');
    this.activeIndex = -1;
  }

  selectSearchEntry(entry) {
    this.searchEl.value = entry.label;
    this.pushRecent(entry);
    this.closeResults();

    if (entry.type === 'store') {
      if (this.isMobile() && this.currentView() === 'map') this.handlePinClick(entry.storeId);
      else this.openSheet(entry.storeId, true);
      return;
    }
    /* place → resolve geometry, recenter + distance-sort (reuse existing) */
    if (!entry.placeId || !this.placesService) return;
    this.placesService.getDetails({ placeId: entry.placeId, fields: ['geometry'] }, (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
      if (!place || !place.geometry || !place.geometry.location) return;
      this.center = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
      this.applyFilter();
      this.recenter();
    });
  }

  onSearchKeydown(event) {
    if (!this.resultsEl || this.resultsEl.hidden) return;
    const rows = Array.from(this.resultsEl.querySelectorAll('[role="option"]'));
    if (!rows.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const dir = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + dir + rows.length) % rows.length;
      rows.forEach((row, index) => row.setAttribute('aria-selected', String(index === this.activeIndex)));
      rows[this.activeIndex].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      if (this.activeIndex >= 0 && rows[this.activeIndex]) {
        event.preventDefault();
        rows[this.activeIndex].click();
      }
    } else if (event.key === 'Escape') {
      this.closeResults();
    }
  }

  recenter() {
    if (!this.map || !this.center) return;
    this.map.setCenter(this.center);
    this.map.setZoom(12);
  }

  fitBounds() {
    if (!this.map || this.markers.size === 0 || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    this.markers.forEach((marker) => {
      if (marker.getMap()) bounds.extend(marker.getPosition());
    });
    if (!bounds.isEmpty()) this.map.fitBounds(bounds);
  }

  /* ---- Store-information sheet ---- */

  /* Pin tap: on mobile MAP view, reveal the store's card below the map (Figma --13);
   * otherwise (desktop, or list view) open the full store-information sheet. */
  handlePinClick(storeId) {
    this.currentStoreId = storeId;
    this.highlightMarker(storeId);
    if (this.isMobile() && this.currentView() === 'map') {
      this.showSelectedCardBelowMap(storeId);
    } else {
      this.openSheet(storeId, true);
    }
  }

  openSheet(storeId, fromPin) {
    const store = this.stores.find((item) => item.id === storeId);
    if (!store || !this.sheetEl) return;

    /* Remember the view to restore on close (don't capture 'detail' itself when
     * switching directly from one open store to another). */
    if (this.layoutEl) {
      const current = this.layoutEl.getAttribute('data-store-view');
      if (current !== 'detail') this.viewBeforeSheet = current;
    }

    this.currentStoreId = storeId;
    /* Store name appears twice (Figma): the panel header title + the first line
     * of the address block. Fill every [data-sheet-name]. */
    this.sheetEl.querySelectorAll('[data-sheet-name]').forEach((el) => {
      el.textContent = store.name || '';
    });
    /* Address as separate lines (Figma: street line(s) + district/postal line). */
    const addressLines = [];
    if (store.address1) addressLines.push(store.address1);
    if (store.address2) addressLines.push(store.address2);
    const districtLine = [store.district, store.city, store.postal]
      .filter((part) => part && String(part).trim() !== '')
      .join(', ');
    if (districtLine) addressLines.push(districtLine);
    this.fill('[data-sheet-address]', addressLines.join('\n'));

    const labels = {
      closed: this.sheetEl.dataset.labelClosed || '',
      yes: this.sheetEl.dataset.labelYes || '',
      no: this.sheetEl.dataset.labelNo || '',
      phone: this.sheetEl.dataset.labelPhone || '',
    };

    const phoneEl = this.sheetEl.querySelector('[data-sheet-phone]');
    if (phoneEl) {
      if (store.phone) {
        const phoneText = phoneEl.querySelector('[data-sheet-phone-text]') || phoneEl;
        phoneText.textContent = store.phone; /* label is a static line in the markup (Figma 2 lines) */
        phoneEl.href = `tel:${String(store.phone).replace(/\s+/g, '')}`;
        phoneEl.hidden = false;
      } else {
        phoneEl.hidden = true;
      }
    }

    const chatEl = this.sheetEl.querySelector('[data-sheet-chat]');
    if (chatEl) {
      if (store.chatUrl) {
        chatEl.href = store.chatUrl;
        chatEl.hidden = false;
      } else {
        chatEl.hidden = true;
      }
    }

    const hours = store.hours && typeof store.hours === 'object' ? store.hours : {};
    this.sheetEl.querySelectorAll('[data-day]').forEach((row) => {
      const value = row.querySelector('[data-day-hours]');
      if (!value) return;
      const dayValue = hours[row.dataset.day];
      value.textContent = dayValue && String(dayValue).trim() !== '' ? dayValue : labels.closed;
    });

    const offered = Array.isArray(store.services)
      ? store.services.map((service) => String(service))
      : [];
    this.sheetEl.querySelectorAll('[data-service]').forEach((row) => {
      const value = row.querySelector('[data-service-value]');
      if (!value) return;
      value.textContent = offered.includes(row.dataset.service) ? labels.yes : labels.no;
    });

    const depsEl = this.sheetEl.querySelector('[data-sheet-departments]');
    if (depsEl) {
      depsEl.innerHTML = '';
      (Array.isArray(store.departments) ? store.departments : []).forEach((dep) => {
        const li = document.createElement('li');
        li.textContent = this.departmentLabel(dep);
        depsEl.appendChild(li);
      });
    }

    const dirEl = this.sheetEl.querySelector('[data-sheet-directions]');
    if (dirEl) dirEl.href = directionsUrl(store);

    this.setSheetTab('info');
    this.lastFocus = document.activeElement;
    this.sheetEl.hidden = false;
    this.sheetEl.setAttribute('aria-hidden', 'false');
    if (this.blanketEl) this.blanketEl.hidden = false;
    const closeBtn = this.sheetEl.querySelector('[data-sheet-close]');
    if (closeBtn) closeBtn.focus();

    if (fromPin) this.scrollToCard(storeId);
    this.highlightMarker(storeId);

    /* Desktop: detail replaces the list in the right rail (set AFTER setSheetTab,
     * which would otherwise reset the view to 'list'). Cosmetic on mobile (sheet overlays). */
    if (this.layoutEl) this.layoutEl.setAttribute('data-store-view', 'detail');
  }

  closeSheet() {
    if (!this.sheetEl || this.sheetEl.hidden) return;
    this.unmountMapFromSheet(); /* return the map to the page column before hiding the sheet */
    this.sheetEl.hidden = true;
    this.sheetEl.setAttribute('aria-hidden', 'true');
    if (this.blanketEl) this.blanketEl.hidden = true;
    /* Restore the list (or the map view that was active before opening). */
    if (this.layoutEl) this.layoutEl.setAttribute('data-store-view', this.viewBeforeSheet || 'list');
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
  }

  fill(selector, value) {
    const el = this.sheetEl.querySelector(selector);
    if (el) el.textContent = value || '';
  }

  highlightMarker(storeId) {
    const store = this.stores.find((item) => item.id === storeId);
    const marker = this.markers.get(storeId);
    if (!store || !marker || !this.map) return;
    this.map.panTo({ lat: store.lat, lng: store.lng });
  }

  scrollToCard(storeId) {
    const card = this.root.querySelector(`[data-store-card][data-store-id="${storeId}"]`);
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

if (ROOT) new StoreLocator(ROOT);
